import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Col, Row} from "react-bootstrap";
import Swal from "sweetalert2";
import {DataGrid, Tooltip} from "devextreme-react";
import {useNavigate} from "react-router-dom";
import {
    Column,
    ColumnChooser,
    Grouping,
    GroupPanel,
    Pager,
    Paging,
    SearchPanel,
} from "devextreme-react/data-grid";
import CustomStore from "devextreme/data/custom_store";
import {ListViewBreadcrumb} from "./ListViewBreadcrumb";
import {IListViewField} from "./ILIstViewField";
import {toast} from "react-toastify";
import {IPostDocumentResponseDto} from "../../dtos/documents/IPostDocumentResponseDto";
import {addMessage} from "../../redux/slices/userMessageSlice";
import {useDispatch} from "react-redux";
import {NewDocumentModalView} from "./NewDocumentModalView";
import {IAppEntity} from "../../config/entities";
import SuccessIcon from "../../assets/img/svg/SuccessIcon";
import {IListViewCreateMoreActions} from "./IListViewCreateMoreAction";
import {IListViewCreateDependedDocumentAction} from "./IListViewCreateDependedDocumentAction";

interface IProps {
    fields: IListViewField[];
    createDocumentActions?: IListViewCreateDependedDocumentAction[];
    entity: any;
    createMoreActions?: IListViewCreateMoreActions[];
    handleUpdateTitle?: (newTitle: string) => void;
}

export const OldNewDocumentListView = ({
                                           fields,
                                           entity,
                                           createDocumentActions,
                                           createMoreActions,
                                           handleUpdateTitle,
                                       }: IProps) => {
    const navigate = useNavigate();

    const pageSizes = [10, 25, 50, 100];
    const dataGridRef = useRef<DataGrid | null>(null);
    const [gridUpdateTrigger, setGridUpdateTrigger] = useState<number>(0);

    const [createDocumentActionVisible, setCreateDocumentActionVisible] =
        useState<boolean>(false);
    const [createMoreActionsVisible, setCreateMoreActionsVisible] =
        useState<boolean>(false);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFailure, setIsFailure] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [isDetailViewCreateDocumentOpen, setIsDetailViewCreateDocumentOpen] =
        useState<boolean>(false);
    const [createDocumentEntity, setCreateDocumentEntity] =
        useState<IAppEntity | null>(null);

    const [isDetailViewOpen, setIsDetailViewOpen] = useState<boolean>(false);
    const [selectedItemId, setSelectedItemId] = useState<string | undefined>(
        undefined
    );

    const [clonedObject, setClonedObject] = useState<any>(null);
    const dispatch = useDispatch();

    const customDataSource = new CustomStore({
        key: "id",
        loadMode: "raw", // omit in the DataGrid, TreeList, PivotGrid, and Scheduler
        cacheRawData: true,
        load: (options) => {
            const dataService = entity.getDataService();
            return dataService.GetAll();
        },
    });

    const handleEditClick = () => {
        setClonedObject(undefined);
        setIsDetailViewOpen(true);
    };

    const handleDeleteClick = async () => {
        if (!selectedItemId) {
            return;
        }

        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            const dataService = entity.getDataService();
            await dataService.Delete(selectedItemId);
            setSelectedItemId(undefined);
            setGridUpdateTrigger(gridUpdateTrigger + 1);
            await Swal.fire("Deleted!", "Your file has been deleted.", "success");
        }
    };

    const handleCreateNewClick = () => {
        setSelectedItemId(undefined);
        setIsDetailViewOpen(true);
        setClonedObject(undefined);
    };

    const handleCLoneClick = async () => {
        const dataService = entity.getDataService();
        const clonedObject = await dataService.Clone(selectedItemId);
        setClonedObject(clonedObject);
        setIsDetailViewOpen(true);
    };

    const handleCreateDocumentActionPopupButtonClick = () => {
        setCreateDocumentActionVisible(!createDocumentActionVisible);
    };

    const handleclickk = () => {
        setCreateMoreActionsVisible(!createMoreActionsVisible);
    };

    const handleCreateDocumentAction = async (
        action: IListViewCreateDependedDocumentAction
    ) => {
        setCreateDocumentActionVisible(false);
        setCreateDocumentEntity(action.appEntity);
        setIsDetailViewCreateDocumentOpen(true);
    };

    const handleCloseDetailView = async (result: boolean) => {
        setIsDetailViewCreateDocumentOpen(false);
        setIsDetailViewOpen(false);
        if (result) {
            setGridUpdateTrigger(gridUpdateTrigger + 1);
        }
    };

    const handleOpenDocumentTransactions = () => {
        if (!selectedItemId) {
            return;
        }
        navigate(`/app/transactions/${selectedItemId}`);
    };

    // More actions
    const handleOpenMoreActions = () => {
        if (!selectedItemId) {
            return;
        }
        navigate("/app/more-actions");
    };

    //
    const handlePostDocumentClick = async () => {
        if (!selectedItemId) {
            return;
        }
        const dataService = entity.getDataService();
        // await dataService.PostDocument(selectedItemId);
        try {
            setIsLoading(true);
            setIsFailure(false);
            setErrorMessage(null);
            const responseDto: IPostDocumentResponseDto =
                await dataService.PostDocument(selectedItemId);
            if (responseDto.success) {
                toast("Posted successfully");
            } else {
                toast.error("Posting failed");
                responseDto.validationErrors.forEach((error: string) => {
                    dispatch(
                        addMessage({
                            message: error,
                            type: "error",
                            date: new Date(),
                            markedAsRead: false,
                        })
                    );
                });
            }
        } catch (e: any) {
            setIsFailure(true);
            setErrorMessage(e.message);
            toast.error(e.message);
        } finally {
            setIsLoading(false);
        }
        setGridUpdateTrigger(gridUpdateTrigger + 1);
    };

    const handleCancelDocumentClick = async () => {
        if (!selectedItemId) {
            return;
        }
        const dataService = entity.getDataService();
        await dataService.CancelDocument(selectedItemId);
        setGridUpdateTrigger(gridUpdateTrigger + 1);
        toast.success("Document cancelled successfully");
    };

    const renderDataGrid = useCallback(
        (fields: IListViewField[]) => {
            return (
                <DataGrid
                    ref={dataGridRef}
                    dataSource={customDataSource}
                    sorting={{mode: "multiple"}}
                    allowColumnReordering={true}
                    rowAlternationEnabled={false}
                    showBorders={false}
                    showRowLines={true}
                    selection={{mode: "single"}}
                    loadPanel={{enabled: true, showIndicator: true, showPane: false}}
                    onSelectionChanged={async (e) => {
                        if (e.selectedRowKeys && e.selectedRowKeys.length > 0) {
                            setSelectedItemId(e.selectedRowKeys[0]);
                        }
                    }}
                >
                    <ColumnChooser enabled={true}/>
                    <GroupPanel visible={true}/>
                    <SearchPanel visible={true} highlightCaseSensitive={true}/>
                    <Grouping autoExpandAll={false}/>

                    <Column
                        dataField="statusName"
                        dataType="string"
                        caption={"Status"}
                        width={75}
                        cellRender={(column: any) => {
                            let statusName = "draft"; //cellElement.data.statusName;
                            let statusColor = "black"; //cellElement.data.statusColor;
                            switch (column.data.status) {
                                case 0:
                                    statusName = "Draft";
                                    statusColor = "black";
                                    break;
                                case 1:
                                    statusName = "Posted";
                                    statusColor = "green";
                                    break;
                                case 2:
                                    statusName = "Deleted";
                                    statusColor = "red";
                                    break;
                                default:
                                    statusName = "Draft";
                                    statusColor = "black";
                                    break;
                            }
                            return <div style={{color: statusColor}}>{statusName}</div>;
                        }}
                    />

                    <Column
                        dataField="documentDate"
                        dataType="datetime"
                        sortOrder={"asc"}
                        sortIndex={0}
                        allowSorting={true}
                    />
                    <Column
                        dataField="documentNumber"
                        dataType="string"
                        sortOrder={"asc"}
                        sortIndex={1}
                    />

                    {fields.map((field, index) => (
                        <Column
                            key={index}
                            dataField={field.fieldName}
                            dataType={field.fieldType}
                        />
                    ))}

                    <Pager allowedPageSizes={pageSizes} showPageSizeSelector={true}/>
                    <Paging defaultPageSize={10}/>
                </DataGrid>
            );
        },
        [gridUpdateTrigger]
    );

    return (
        <>
            <div className="main-container container-fluid pt-2">
                <div className="card card-body border">
                    <Row className={"row-sm"}>
                        <Col lg={12}>
                            <div className={"d-flex"}>
                                <Button
                                    size={"sm"}
                                    variant=""
                                    className="btn btn-primary mb-3"
                                    onClick={handleCreateNewClick}
                                >
                                    Add New Row
                                </Button>
                                <div className={"flex-grow-1"}></div>

                                <Button
                                    size={"sm"}
                                    variant=""
                                    className="btn btn-light mb-3 me-2"
                                    disabled={!selectedItemId}
                                    onClick={handleCLoneClick}
                                >
                                    Clone
                                </Button>

                                {createDocumentActions &&
                                    createDocumentActions.length > 0 && (
                                        <>
                                            <Button
                                                id={"createDocumentActionsButton"}
                                                size={"sm"}
                                                variant=""
                                                className="btn btn-light mb-3 me-2"
                                                disabled={!selectedItemId}
                                                onClick={handleCreateDocumentActionPopupButtonClick}
                                            >
                                                Create document ...
                                            </Button>
                                            <Tooltip
                                                target="#createDocumentActionsButton"
                                                position="bottom"
                                                visible={createDocumentActionVisible}
                                                onHidden={() => {
                                                    setCreateDocumentActionVisible(false);
                                                }}
                                                hideOnOutsideClick={true}
                                            >
                                                <b>
                                                    {createDocumentActions.map((action, index) => (
                                                        <div className={"text-start my-2"} key={index}>
                                                            <Button
                                                                size={"sm"}
                                                                variant=""
                                                                className="btn btn-light w-100"
                                                                disabled={!selectedItemId}
                                                                onClick={() =>
                                                                    handleCreateDocumentAction(action)
                                                                }
                                                            >
                                                                {action.title}
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </b>
                                            </Tooltip>
                                        </>
                                    )}

                                <Button
                                    size={"sm"}
                                    variant=""
                                    className="btn btn-light mb-3 me-2"
                                    disabled={!selectedItemId}
                                    onClick={handleEditClick}
                                >
                                    Edit
                                </Button>
                                <Button
                                    size={"sm"}
                                    variant=""
                                    className="btn btn-light mb-3 me-2"
                                    onClick={handleDeleteClick}
                                    disabled={!selectedItemId}
                                >
                                    Delete
                                </Button>
                                <Button
                                    size={"sm"}
                                    variant=""
                                    className="btn btn-light mb-3 me-2"
                                    disabled={!selectedItemId}
                                    onClick={handlePostDocumentClick}
                                >
                                    Post
                                </Button>
                                <Button
                                    size={"sm"}
                                    variant=""
                                    className="btn btn-light mb-3 me-2"
                                    disabled={!selectedItemId}
                                    onClick={handleCancelDocumentClick}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size={"sm"}
                                    variant=""
                                    className="btn btn-light mb-3 me-2"
                                    disabled={!selectedItemId}
                                    onClick={handleOpenDocumentTransactions}
                                >
                                    Transactions
                                </Button>

                                <>
                                    <Button
                                        id={"moreAction"}
                                        size={"sm"}
                                        variant=""
                                        className="btn btn-light mb-3 me-2"
                                        disabled={!selectedItemId}
                                        onClick={handleclickk}
                                    >
                                        More actions ...
                                    </Button>
                                    <Tooltip
                                        target="#moreAction"
                                        position="bottom"
                                        visible={createMoreActionsVisible}
                                        onHidden={() => {
                                            setCreateMoreActionsVisible(false);
                                        }}
                                        hideOnOutsideClick={true}
                                    >
                                        <b>
                                            {createMoreActions?.map((action, index) => (
                                                <div className={"text-start my-2"} key={index}>
                                                    <Button
                                                        size={"sm"}
                                                        variant=""
                                                        className="btn btn-light w-100"
                                                        disabled={!selectedItemId}
                                                        onClick={handleOpenMoreActions}
                                                    >
                                                        {action.title}
                                                    </Button>
                                                </div>
                                            ))}
                                        </b>
                                    </Tooltip>
                                </>
                            </div>
                        </Col>
                    </Row>
                    <Row className={"row-sm"} onDoubleClick={handleEditClick}>
                        <Col lg={12}>{renderDataGrid(fields)}</Col>
                    </Row>
                    <Row>
                        <Col lg={12}>
                            {/*{isLoading && (<b>Loading...</b>)}*/}
                            {isFailure && <b>{errorMessage}</b>}
                        </Col>
                    </Row>
                </div>
            </div>
            {isDetailViewOpen && (
                <NewDocumentModalView
                    entity={entity}
                    onClose={handleCloseDetailView}
                    id={selectedItemId}
                    clonedObject={clonedObject}
                />
            )}
            {isDetailViewCreateDocumentOpen && (
                <NewDocumentModalView
                    entity={createDocumentEntity}
                    onClose={handleCloseDetailView}
                    id={undefined}
                    clonedObject={undefined}
                />
            )}
        </>
    );
};
